// L'OPPORTUNITÉ d'un insight (migration 048) : qui on vise, ce qu'on parie,
// et l'indicateur qui tranchera.
//
// Lecture d'abord : trois lignes, et ce qui manque pour que l'opportunité soit
// vérifiable. « Compléter » ouvre le formulaire ; « Proposer avec l'IA » le
// pré-remplit à partir du problème et de ses preuves. La proposition n'est
// enregistrée que si quelqu'un la relit et clique sur « Enregistrer ».
import { useId, useState } from 'react';
import { usePersonas, useProposeOpportunity, useUpdateInsight } from '../api/queries';
import { ApiRequestError } from '../api/client';
import type { Insight, SuccessMetric } from '../api/types';
import { Button, Chip, ErrorBanner, Select } from '../components/ui';
import { Icon } from '../components/icons';
import { useMeasureHistory } from '../dashboard/measures';
import { HypothesisTracking } from './HypothesisTracking';

interface Draft {
  targets: string[];
  hypothesis: string;
  label: string;
  unit: string;
  baseline: string;
  target: string;
  due: string;
  /** Clé de la mesure suivie ('' = aucune). */
  key: string;
}

const num = (n: number | null) => (n === null ? '' : String(n).replace('.', ','));

function toDraft(insight: Insight, defaultTargets: string[]): Draft {
  const m = insight.success_metric;
  return {
    targets: insight.target_persona_ids.length > 0 ? insight.target_persona_ids : defaultTargets,
    hypothesis: insight.hypothesis ?? '',
    label: m?.label ?? '',
    unit: m?.unit ?? '',
    baseline: m ? num(m.baseline) : '',
    target: m ? num(m.target) : '',
    due: m?.due ?? '',
    key: m?.key ?? '',
  };
}

/** « 30 % → 80 % d'ici le 31/12/2026 » : la métrique se lit en une ligne. */
function metricLine(m: SuccessMetric): string {
  const u = m.unit ? ` ${m.unit}` : '';
  const from = m.baseline !== null ? `${num(m.baseline)}${u}` : 'départ à mesurer';
  const to = m.target !== null ? `${num(m.target)}${u}` : 'cible à fixer';
  const due = m.due ? ` d’ici le ${m.due.split('-').reverse().join('/')}` : '';
  return `${from} → ${to}${due}`;
}

function errorMessage(cause: unknown): string {
  if (cause instanceof ApiRequestError) return cause.message;
  return 'Une erreur inattendue est survenue.';
}

export function OpportunityPanel({ insight, projectId, canWrite }: { insight: Insight; projectId: string; canWrite: boolean }) {
  const personas = usePersonas(projectId).data ?? [];
  const update = useUpdateInsight(projectId);
  const propose = useProposeOpportunity();
  // Les mesures suivies du projet (connecteurs, saisies) : la métrique peut s'y relier.
  const measures = useMeasureHistory(projectId).data?.measures ?? [];
  const [draft, setDraft] = useState<Draft | null>(null);
  // La justification de l'IA, affichée tant que sa proposition n'est pas enregistrée.
  const [proposed, setProposed] = useState<string | null | undefined>(undefined);
  const baseId = useId();

  const nameOf = new Map(personas.map((p) => [p.id, p.name]));
  // Par défaut, on vise les personas que désignent les preuves.
  const fromEvidence = personas.filter((p) => insight.problem.personas.includes(p.name)).map((p) => p.id);
  const supported = insight.evidence_count > 0;
  const { missing } = insight.opportunity_check;

  const set = (patch: Partial<Draft>) => setDraft((d) => (d ? { ...d, ...patch } : d));

  async function askAi() {
    const base = draft ?? toDraft(insight, fromEvidence);
    try {
      const res = await propose.mutateAsync(insight.id);
      const p = res.proposal;
      if (!p) { setDraft(base); setProposed(null); return; }
      const m = p.successMetric;
      setDraft({
        ...base,
        targets: p.targetPersonaIds.length > 0 ? p.targetPersonaIds : base.targets,
        hypothesis: p.hypothesis ?? base.hypothesis,
        label: m?.label ?? base.label,
        unit: m?.unit ?? base.unit,
        target: m && m.target !== null ? num(m.target) : base.target,
      });
      setProposed(p.rationale ?? '');
    } catch {
      /* rendu par le bandeau */
    }
  }

  async function save() {
    if (!draft) return;
    try {
      await update.mutateAsync({
        id: insight.id,
        patch: {
          targetPersonaIds: draft.targets,
          hypothesis: draft.hypothesis,
          // Sans intitulé, le serveur comprend « pas de métrique ».
          successMetric: draft.label.trim()
            ? { label: draft.label, unit: draft.unit, baseline: draft.baseline, target: draft.target, due: draft.due, key: draft.key }
            : null,
        },
      });
      setDraft(null);
      setProposed(undefined);
    } catch {
      /* rendu par le bandeau */
    }
  }

  const toggle = (id: string) =>
    set({ targets: draft?.targets.includes(id) ? draft.targets.filter((t) => t !== id) : [...(draft?.targets ?? []), id] });

  return (
    <section className="opportunity" aria-label="Opportunité">
      <div className="opportunity-head">
        <h4 className="problem-label">Opportunité</h4>
        {missing.length > 0 ? (
          <span className="opportunity-missing">À compléter : {missing.join(', ')}</span>
        ) : (
          <span className="opportunity-ready">Vérifiable</span>
        )}
      </div>

      {draft === null ? (
        <>
          <dl className="problem-facts">
            <div>
              <dt>Cible</dt>
              <dd>
                {insight.target_persona_ids.length > 0
                  ? insight.target_persona_ids.map((id) => nameOf.get(id) ?? id).join(', ')
                  : 'Aucun persona visé'}
              </dd>
            </div>
            <div>
              <dt>Hypothèse</dt>
              <dd>{insight.hypothesis ?? 'Aucune hypothèse formulée'}</dd>
            </div>
            <div>
              <dt>Métrique de succès</dt>
              <dd>
                {insight.success_metric ? (
                  <>
                    {insight.success_metric.label}
                    <br />
                    <span className="problem-formula">{metricLine(insight.success_metric)}</span>
                  </>
                ) : 'Aucune métrique'}
              </dd>
            </div>
          </dl>
          {/* Le suivi : lancer le test, lire l'avant/après, trancher. */}
          {insight.hypothesis ? <HypothesisTracking insight={insight} projectId={projectId} canWrite={canWrite} /> : null}
          {canWrite ? (
            <div className="opportunity-actions">
              <Button variant="ghost" onClick={() => setDraft(toDraft(insight, fromEvidence))}>
                {missing.length > 0 ? 'Compléter' : 'Modifier'}
              </Button>
              {supported ? (
                <Button variant="ghost" loading={propose.isPending} onClick={() => void askAi()}>
                  <Icon name="sparkles" size={14} />
                  Proposer avec l’IA
                </Button>
              ) : null}
            </div>
          ) : null}
        </>
      ) : (
        <div className="opportunity-form">
          {proposed !== undefined ? (
            <p className="opportunity-ai">
              <Chip>IA</Chip>{' '}
              {proposed === null
                ? 'L’IA n’a rien pu proposer d’exploitable : complétez à la main.'
                : `Proposition à relire avant d’enregistrer. ${proposed}`}
            </p>
          ) : null}

          <fieldset className="opportunity-targets">
            <legend className="problem-label">Cible</legend>
            {personas.length === 0 ? (
              <span className="problem-formula">Aucun persona dans ce projet.</span>
            ) : personas.map((p) => (
              <label key={p.id} className="opportunity-target">
                <input className="zy-check" type="checkbox" checked={draft.targets.includes(p.id)} onChange={() => toggle(p.id)} />
                {p.name}
              </label>
            ))}
          </fieldset>

          <div className="problem-field">
            <label className="problem-label" htmlFor={`${baseId}-h`}>Hypothèse</label>
            <textarea
              id={`${baseId}-h`}
              className="zy-input"
              rows={3}
              value={draft.hypothesis}
              placeholder="Si nous [changement], alors [qui] [comportement attendu]."
              onChange={(e) => set({ hypothesis: e.target.value })}
            />
          </div>

          <div className="opportunity-metric">
            <div className="problem-field opportunity-metric-label">
              <label className="problem-label" htmlFor={`${baseId}-l`}>Métrique de succès</label>
              <input id={`${baseId}-l`} className="zy-input" value={draft.label}
                placeholder="Ex. Taux de connexion bancaire réussie" onChange={(e) => set({ label: e.target.value })} />
            </div>
            <div className="problem-field">
              <label className="problem-label" htmlFor={`${baseId}-u`}>Unité</label>
              <input id={`${baseId}-u`} className="zy-input" value={draft.unit} placeholder="%" onChange={(e) => set({ unit: e.target.value })} />
            </div>
            <div className="problem-field">
              <label className="problem-label" htmlFor={`${baseId}-b`}>Départ</label>
              <input id={`${baseId}-b`} className="zy-input" inputMode="decimal" value={draft.baseline} onChange={(e) => set({ baseline: e.target.value })} />
            </div>
            <div className="problem-field">
              <label className="problem-label" htmlFor={`${baseId}-t`}>Cible</label>
              <input id={`${baseId}-t`} className="zy-input" inputMode="decimal" value={draft.target} onChange={(e) => set({ target: e.target.value })} />
            </div>
            <div className="problem-field">
              <label className="problem-label" htmlFor={`${baseId}-d`}>Échéance</label>
              <input id={`${baseId}-d`} className="zy-input" type="date" value={draft.due} onChange={(e) => set({ due: e.target.value })} />
            </div>
          </div>

          {/* Relier la métrique à une mesure suivie : l'avant/après se calcule alors seul. */}
          <div className="problem-field">
            <label className="problem-label" htmlFor={`${baseId}-k`}>Mesure suivie</label>
            <Select
              id={`${baseId}-k`}
             
              value={draft.key}
              onChange={(e) => {
                const m = measures.find((x) => x.key === e.target.value);
                set({
                  key: e.target.value,
                  ...(m && !draft.label.trim() ? { label: m.name } : {}),
                  ...(m && !draft.unit.trim() && m.unit ? { unit: m.unit } : {}),
                });
              }}
            >
              <option value="">{measures.length === 0 ? 'Aucune mesure relevée (tableau de bord)' : 'Aucune — suivi à la main'}</option>
              {measures.map((m) => <option key={m.key} value={m.key}>{m.name} · {m.sourceLabel}</option>)}
            </Select>
          </div>

          <div className="opportunity-actions">
            <Button variant="accent" loading={update.isPending} onClick={() => void save()}>Enregistrer</Button>
            {supported ? (
              <Button variant="ghost" loading={propose.isPending} onClick={() => void askAi()}>
                <Icon name="sparkles" size={14} />
                Proposer avec l’IA
              </Button>
            ) : null}
            <Button variant="ghost" onClick={() => { setDraft(null); setProposed(undefined); }}>Annuler</Button>
          </div>
        </div>
      )}
      {propose.error ? <ErrorBanner>{errorMessage(propose.error)}</ErrorBanner> : null}
      {update.error ? <ErrorBanner>Enregistrement impossible.</ErrorBanner> : null}
    </section>
  );
}
